/**
 * Overlay URL Resolver
 *
 * Utilities for resolving blob URLs to Convex URLs before saving to the database.
 * Ensures that saved overlays have valid, persistent URLs that work across devices.
 *
 * Used by the autosave hook to resolve URLs before syncing to Convex.
 */

import { Overlay, ClipOverlay, SoundOverlay, ImageOverlay, OverlayType } from '../../types';
import { getUserMediaItems, UserMediaItem } from './indexdb';
import { getUserId } from './user-id';

/**
 * Type guard to check if overlay has a src property (media overlay)
 */
export function isMediaOverlay(
  overlay: Overlay
): overlay is ClipOverlay | SoundOverlay | ImageOverlay {
  return (
    overlay.type === OverlayType.VIDEO ||
    overlay.type === OverlayType.SOUND ||
    overlay.type === OverlayType.IMAGE
  );
}

/**
 * Check if a URL is a blob URL
 */
export function isBlobUrl(url: string): boolean {
  return url?.startsWith('blob:') ?? false;
}

/**
 * Check if a URL is a valid Convex/HTTPS URL
 */
export function isConvexUrl(url: string): boolean {
  return url?.startsWith('https://') ?? false;
}

/**
 * Find matching media item in IndexedDB by various criteria
 *
 * Priority:
 * 1. Match by current src URL (blob or convex)
 * 2. Match by file hash
 * 3. Match by file name
 */
async function findMatchingMedia(
  overlay: ClipOverlay | SoundOverlay | ImageOverlay,
  mediaItems: UserMediaItem[]
): Promise<UserMediaItem | null> {
  // Priority 1: Match by current src URL
  const byUrl = mediaItems.find(
    (item) => item.serverPath === overlay.src || item.convexUrl === overlay.src
  );
  if (byUrl) return byUrl;

  // Priority 2: Match by file hash (if overlay has mediaFileHash)
  if ('mediaFileHash' in overlay && overlay.mediaFileHash) {
    const byHash = mediaItems.find((item) => item.fileHash === overlay.mediaFileHash);
    if (byHash) return byHash;
  }

  // Priority 3: Match by file name (if overlay has mediaFileName)
  if ('mediaFileName' in overlay && overlay.mediaFileName) {
    const byName = mediaItems.find((item) => item.name === overlay.mediaFileName);
    if (byName) return byName;
  }

  return null;
}

/**
 * Resolve a single overlay's URL from blob to Convex URL
 *
 * @param overlay - The overlay to resolve
 * @param mediaItems - Array of media items from IndexedDB
 * @returns Updated overlay with resolved URL and metadata, or original if no resolution needed
 */
export function resolveOverlayUrl<T extends ClipOverlay | SoundOverlay | ImageOverlay>(
  overlay: T,
  mediaItems: UserMediaItem[]
): T {
  // Skip if not a blob URL - already has valid Convex URL
  if (!isBlobUrl(overlay.src)) {
    return overlay;
  }

  // Find matching media item
  const matchingMedia = mediaItems.find(
    (item) => item.serverPath === overlay.src || item.convexUrl === overlay.src
  );

  if (!matchingMedia) {
    console.log(`[OverlayUrlResolver] No matching media found for overlay ${overlay.id}`);
    return overlay;
  }

  // Check if media has a Convex URL
  if (!matchingMedia.convexUrl) {
    console.log(
      `[OverlayUrlResolver] Media found but no Convex URL yet for overlay ${overlay.id} (upload status: ${matchingMedia.uploadStatus})`
    );
    // Add metadata even if URL isn't available yet
    return {
      ...overlay,
      mediaStorageId: matchingMedia.convexStorageId,
      mediaFileHash: matchingMedia.fileHash,
      mediaFileName: matchingMedia.name,
    };
  }

  // Resolve blob URL to Convex URL
  console.log(
    `[OverlayUrlResolver] Resolved overlay ${overlay.id}: blob -> ${matchingMedia.convexUrl.substring(0, 50)}...`
  );

  return {
    ...overlay,
    src: matchingMedia.convexUrl,
    mediaStorageId: matchingMedia.convexStorageId,
    mediaFileHash: matchingMedia.fileHash,
    mediaFileName: matchingMedia.name,
  };
}

export interface OverlayResolutionResult {
  overlays: Overlay[];
  resolvedCount: number;
  unresolvedCount: number;
  totalMediaOverlays: number;
}

/**
 * Resolve all overlay URLs before saving to Convex
 *
 * This function:
 * 1. Identifies media overlays with blob URLs
 * 2. Matches them to media items in IndexedDB
 * 3. Replaces blob URLs with Convex URLs where available
 * 4. Adds mediaStorageId, mediaFileHash, mediaFileName metadata
 *
 * @param overlays - Array of overlays to resolve
 * @returns Updated overlays with resolved URLs and resolution statistics
 */
export async function resolveOverlaysForSave(overlays: Overlay[]): Promise<OverlayResolutionResult> {
  // Get media items from IndexedDB
  const userId = getUserId();
  const mediaItems = await getUserMediaItems(userId);

  let resolvedCount = 0;
  let unresolvedCount = 0;
  let totalMediaOverlays = 0;

  const resolvedOverlays = overlays.map((overlay) => {
    // Only process media overlays
    if (!isMediaOverlay(overlay)) {
      return overlay;
    }

    totalMediaOverlays++;

    // Check if this overlay has a blob URL
    if (!isBlobUrl(overlay.src)) {
      // Already resolved, but ensure metadata is present
      const matchingMedia = mediaItems.find(
        (item) =>
          item.serverPath === overlay.src ||
          item.convexUrl === overlay.src ||
          item.convexStorageId === (overlay as any).mediaStorageId
      );

      if (matchingMedia && !overlay.mediaStorageId) {
        return {
          ...overlay,
          mediaStorageId: matchingMedia.convexStorageId,
          mediaFileHash: matchingMedia.fileHash,
          mediaFileName: matchingMedia.name,
        };
      }
      return overlay;
    }

    // Resolve blob URL
    const resolved = resolveOverlayUrl(overlay, mediaItems);

    if (resolved.src !== overlay.src) {
      resolvedCount++;
    } else if (isBlobUrl(resolved.src)) {
      unresolvedCount++;
    }

    return resolved;
  });

  if (resolvedCount > 0 || unresolvedCount > 0) {
    console.log(
      `[OverlayUrlResolver] Resolution complete: ${resolvedCount} resolved, ${unresolvedCount} unresolved (${totalMediaOverlays} total media overlays)`
    );
  }

  return {
    overlays: resolvedOverlays,
    resolvedCount,
    unresolvedCount,
    totalMediaOverlays,
  };
}

/**
 * Check if there are any unresolved blob URLs in the overlays
 *
 * Useful for warning users before navigation if uploads are pending
 *
 * @param overlays - Array of overlays to check
 * @returns True if any overlay has a blob URL that hasn't been uploaded
 */
export function hasUnresolvedBlobUrls(overlays: Overlay[]): boolean {
  return overlays.some((overlay) => {
    if (!isMediaOverlay(overlay)) return false;
    return isBlobUrl(overlay.src);
  });
}

/**
 * Get list of overlays with unresolved blob URLs
 *
 * @param overlays - Array of overlays to check
 * @returns Array of overlay IDs that have blob URLs
 */
export function getUnresolvedOverlays(overlays: Overlay[]): number[] {
  return overlays
    .filter((overlay) => isMediaOverlay(overlay) && isBlobUrl(overlay.src))
    .map((overlay) => overlay.id);
}

/**
 * Get count of pending uploads (overlays with blob URLs)
 *
 * @param overlays - Array of overlays to check
 * @returns Count of overlays with blob URLs
 */
export function getPendingUploadCount(overlays: Overlay[]): number {
  return overlays.filter((overlay) => isMediaOverlay(overlay) && isBlobUrl(overlay.src)).length;
}
